import React, { useState } from "react";
import { Button, FormGroup, Modal, TextInput, TextArea } from "src/components";
import Axios from "src/services/api";
import { toast } from "react-hot-toast";
import styles from "./AccountApprovalModal.module.scss";

interface AccountApprovalData {
  contactEmail: string;
  website: string;
  phoneNumber: string;
  productsSold: string;
}

interface AccountApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userData?: any;
}

const AccountApprovalModal: React.FC<AccountApprovalModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userData,
}) => {
  const [formData, setFormData] = useState<AccountApprovalData>({
    contactEmail: userData?.contactEmail || "",
    website: userData?.website || "",
    phoneNumber: userData?.phoneNumber || "",
    productsSold: userData?.productsSold || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (key: keyof AccountApprovalData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.contactEmail) {
      newErrors.contactEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "Invalid email format";
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    }

    if (!formData.productsSold) {
      newErrors.productsSold = "Please describe the products you sell";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await Axios.post("/users/request-account-approval", formData)
        .then(() => {
          toast.success(
            "Information submitted successfully! We'll review your account shortly."
          );
          onSuccess();
          onClose();
        })
        .catch((err) => {
          toast.error("Failed to submit information");
        })
        .finally(() => {
          setIsSubmitting(false);
          window.location.reload();
        });
    } catch (error) {
      console.error("Error submitting account information:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title="Account Approval"
      onClose={onClose}
      footerRightButton1={{
        label: "Cancel",
        onClick: onClose,
      }}
      footerRightButton2={{
        label: isSubmitting ? "Submitting..." : "Submit",
        onClick: handleSubmit,
        disabled: isSubmitting,
      }}
    >
      <div className={styles.modalContent}>
        <p className={styles.infoText}>
          Please provide the following information to complete your account
          approval process.
        </p>

        <FormGroup
          label="Email to be contacted"
          error={errors.contactEmail}
          required
        >
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flexGrow: 1 }}>
              <TextInput
                name="contactEmail"
                value={formData.contactEmail}
                onChange={(e) => handleChange("contactEmail", e.target.value)}
                error={errors.contactEmail}
                placeholder="e.g. contact@example.com"
                type="email"
              />
            </div>
            <Button
              text="Use account email"
              variant="secondary"
              onClick={() =>
                handleChange("contactEmail", userData?.email || "")
              }
            />
          </div>
        </FormGroup>

        <FormGroup label="Business/Personal Website" error={errors.website}>
          <TextInput
            name="website"
            value={formData.website}
            onChange={(e) => handleChange("website", e.target.value)}
            error={errors.website}
            placeholder="e.g. https://www.example.com"
          />
        </FormGroup>

        <FormGroup label="Phone Number" error={errors.phoneNumber} required>
          <TextInput
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={(e) => handleChange("phoneNumber", e.target.value)}
            error={errors.phoneNumber}
            placeholder="Enter the country code as well. e.g. +1 123-456-7890"
          />
        </FormGroup>

        <FormGroup
          label="Products that you will sell"
          error={errors.productsSold}
          required
        >
          <TextArea
            name="productsSold"
            value={formData.productsSold}
            onChange={(e) => handleChange("productsSold", e.target.value)}
            placeholder="Please describe the products or services you offer (e.g. 'I sell courses on How to fly like Goku')"
            rows={4}
          />
        </FormGroup>
      </div>
    </Modal>
  );
};

export default AccountApprovalModal;
